import { Injectable } from '@angular/core';
import { dependencies } from '../../class/workflow.dependency';
import { CustomEnumConfig, LogicFnRef, PrimitiveType, WorkflowCard, WorkflowField } from '../../class/workflow.type';
import { StatefulValue } from '../../class/stateful-value.class';
import { FieldInstance } from '@workflow/class/field.service';
import { PageInstance } from '@workflow/class/page.service';
import { CardInstance } from '@workflow/class/card.service';
import { FlowInstance } from '@workflow/class/flow.service';

export interface LogicFnContext {
  // eslint-disable-next-line @typescript-eslint/ban-types
  route?: {};
  functionName?: string;
  card?: CardInstance;
  previousCards?: CardInstance[];
  field?: FieldInstance;
  customEnumConfig?: CustomEnumConfig;
  flow?: FlowInstance;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  routeEvent?: any;
  iteration?: number;
  defaultNavigationFn?: () => void;
  iteratedCardId?: string;
  page?: PageInstance;
  targetPage?: PageInstance;
  pageIteration?: number;
  offset?: number;
  value?: StatefulValue;
}

export type LogicFn = (context: LogicFnContext, ...args: PrimitiveType[]) => boolean;
interface LogicFnRepo {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [fnName: string]: LogicFn | [LogicFn, {}];
}

type Desc = LogicFnRef | LogicFnRef[];
type Collect = 'collect' | 'single' | 'generic';
type BreakDesc = boolean | Collect;
type Evaluate = void | boolean | string[];

/**
 * Register and call "logic functions"
 */
@Injectable({
  providedIn: 'root'
})
export class LogicService {
  /**
   * keeps track of the latest employment card being added.
   * When available, remove and change this variable to a stateful value variable.
   */
  public lastEmpCardIndex: number;

  /**
   * Logic function registry, including some generic functions
   */
  private readonly fns: LogicFnRepo = {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    always: (_ctx) => true,
    false: (_ctx, val) => val === false,
    falsy: (_ctx, val) => !val || (val && val.toString() === 'false'),
    in: (_ctx, val, ...matches: PrimitiveType[]) => matches.indexOf(val) > -1,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    never: (_ctx) => false,
    set: (_ctx, val) => val != null,
    true: (_ctx, val) => val === true,
    truthy: (_ctx, val) => !!val || (val && val.toString() === 'true'),
    unset: (_ctx, val) => val == null,

    // usage example in .config: ["eq", "123", "213"]
    // double-quote string values to distinguish from storage endpoints. Ex: "\"stringVal\""
    eq: (_ctx, l, r) => l === r,
    gt: (_ctx, l, r) => l > r,
    gte: (_ctx, l, r) => l >= r,
    lt: (_ctx, l, r) => l < r,
    lte: (_ctx, l, r) => l <= r,
    ne: (_ctx, l, r) => l !== r
  };

  /**
   * return the arguments of the first logic function if the function is found
   * @param fnName the name of the function to search
   * @param configSources list of logic functions in the config source
   */
  public hasFunction(fnName: string, ...configSources: Desc[]): PrimitiveType[] {
    for (const configSource of configSources) {
      for (const desc of Array.isArray(configSource) ? configSource : [configSource]) {
        if (Array.isArray(desc) && desc[0] === fnName) {
          return desc.slice(1);
        } else if (desc === fnName) {
          return [];
        }
      }
    }
  }

  /**
   * Call a registered logic function
   * @param fnName function name
   * @param context caller context info (e.g. field or page etc)
   * @param breakOnFalsy for AND style condition function list, or 'collect' to collect fnNames of failed validations
   * @param iterationId an optional iteration id to override the context iteration id
   * @param pageIterationId an optional page iteration id to override the context page iteration id
   */
  public evaluate(desc: Desc, context: LogicFnContext, breakOnFalsy?: false, iterationId?: number, pageIterationId?: number): void;
  public evaluate(desc: Desc, context: LogicFnContext, breakOnFalsy: true, iterationId?: number, pageIterationId?: number): boolean;
  public evaluate(desc: Desc, context: LogicFnContext, collectFails: Collect, iterationId?: number, pageIterationId?: number): string[];
  public evaluate<T>(desc: Desc, context: LogicFnContext, collectFails: Collect, iterationId?: number, pageIterationId?: number): T[];
  public evaluate(desc: Desc, context: LogicFnContext, breakOnFalsy?: BreakDesc, iterationId?: number, pageIterationId?: number): Evaluate {
    const collection = [];
    if (desc?.length) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/tslint/config
      for (const logicFnRef of <LogicFnRef[]>(Array.isArray(desc[0]) ? desc : [desc])) {
        const [fnName, ...args] = logicFnRef;
        const len = args.length;
        for (let i = 0; i < len; i++) {
          const ref = context.field || context.card;
          const iterationIdVal = iterationId ?? ref?.iterationId;

          const pageRef = context.field || context.card || context.page;
          const pageIterationIdVal = pageIterationId ?? pageRef?.pageIterationId;

          args[i] = dependencies.stringVariableService.parse(args[i], iterationIdVal, pageIterationIdVal);
        }

        const inf = this.fn(fnName);

        if (!inf) {
          throw Error(`Logic function ${fnName} has not been registered. `);
        }
        context.functionName = fnName;
        const res = typeof inf === 'function' ? inf(context, ...args) : inf[0].apply(inf[1], [context, ...args]);
        if (breakOnFalsy === 'collect') {
          if (res === false) {
            collection.push(fnName);
          }
        } else if (breakOnFalsy === 'single') {
          collection.push(res);
        } else if (breakOnFalsy === 'generic') {
          Array.isArray(res) ? collection.push(...res) : collection.push(res);
        } else if (breakOnFalsy && !res) {
          return false;
        }
      }
    }

    return breakOnFalsy === 'collect' || breakOnFalsy === 'single' || breakOnFalsy === 'generic'
      ? collection
      : breakOnFalsy
      ? true
      : undefined;
  }

  /**
   * Inject an app-specific collection of logic functions
   * @param service reference to container service. Will also serve as scope when calling functions
   */
  // tslint:disable-next-line:no-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
  public register(service: { [fnName: string]: LogicFn | any }): void {
    for (const fnName in service) {
      if (typeof service[fnName] === 'function' && fnName !== 'constructor') {
        this.addFn(fnName, service);
      }
    }
  }

  /**
   * Allow testing the legitimacy and availability of a LogicFnRef in the JSON config
   * Will throw error on fail
   * @param logicFnRef data to validate
   * @param container name of the config segment (i.e 'field', 'card', ...). Used for error message
   * @param property property of container config (i.e 'enabled', 'visible', ...). Used for error message
   * @param id id of container (i.e 'fieldId', 'cardId', ...). Used for error message
   * @param singular does not support an array of logic function
   */
  public testConfig(config: WorkflowField | WorkflowCard, container: string, property: string, id: string, singular = false): void {
    if (config[property]) {
      if (!singular && !Array.isArray(config[property][0])) {
        config[property] = [config[property]];
      }
      for (const logicFnRef of singular ? [config[property]] : config[property]) {
        if (!Array.isArray(logicFnRef) || !logicFnRef[0] || typeof logicFnRef[0] !== 'string') {
          throw new Error(`Property "${property}" on ${container} "${id}" (${JSON.stringify(logicFnRef)}) must be ["fnName", ...args]`);
        }
        if (!this.fn(logicFnRef[0])) {
          // Temporarily reporting this as a console.warn, as a error throw currently is causing
          // unit tests to fail. This is a known issue and will be addressed when we can ensure all
          // unit tests are properly mocking the logic service/workflowservice
          console.warn(`Logic function referenced for "${property}" on ${container} "${id}" (${logicFnRef[0]}) is not registered`);
          // throw new Error(`Logic function referenced for "${property}" on ${container} "${id}" (${logicFnRef[0]}) is not registered`);
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  private fn(fnName: string): LogicFn | [LogicFn, {}] {
    return this.fns[fnName];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
  private addFn(fnName: string, service: { [fnName: string]: LogicFn | any }): void {
    if (this.fn(fnName)) {
      console.warn('Logic function name collision: ' + fnName);
      return;
    }

    this.fns[fnName] = [service[fnName], service];
  }
}
